/* global google */
import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    InputAdornment,
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    Autocomplete,
    Avatar,
    Divider,
    Select,
    FormControl,
    InputLabel,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { googleMapsConfig } from '../../firebase/config';

const AddRegularTournamentForm = ({
    open,
    onClose,
    onSubmit,
    newRegularTournament,
    handleInputChange,
    handleOrganizerSelect,
    organizers,
    handleAddPrize,
    handlePrizeChange,
    handleRemovePrize,
    handleOpenOrganizerDialog,
    isEditing
}) => {
    const locationNameInputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [mapsError, setMapsError] = useState(null);

    // Style dla pól tekstowych
    const commonTextFieldStyles = {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'rgb(55, 65, 81)',
            },
            '&:hover fieldset': {
                borderColor: 'rgb(75, 85, 99)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'rgb(196, 181, 253)',
            },
            '& input': {
                color: 'white',
            },
            '& textarea': {
                color: 'white',
            },
            '& .MuiSelect-icon': {
                color: 'rgb(196, 181, 253)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgb(156, 163, 175)',
            '&.Mui-focused': {
                color: 'rgb(196, 181, 253)',
            },
        },
        '& .MuiSelect-select': {
            color: 'white',
        },
    };

    // Inicjalizacja Google Maps
    useEffect(() => {
        const checkGoogleMapsLoaded = () => {
            if (window.google && window.google.maps) {
                setMapsLoaded(true);
                setMapsError(null);
            } else {
                setTimeout(checkGoogleMapsLoaded, 100);
            }
        };

        if (open) {
            checkGoogleMapsLoaded();
        }

        return () => {
            cleanupAutocomplete();
        };
    }, [open]);

    const cleanupAutocomplete = () => {
        if (autocompleteRef.current) {
            google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
    };

    // Obsługa linku Google Maps
    const handleGoogleMapsLink = async (link) => {
        try {
            const url = new URL(link);
            const pathParts = url.pathname.split('/');
            const placeId = pathParts[pathParts.length - 1];

            if (placeId && placeId.length > 10) {
                const geocoder = new google.maps.Geocoder();
                const result = await geocoder.geocode({ placeId });

                if (result.results[0]) {
                    const place = result.results[0];
                    const location = place.geometry.location;

                    handleInputChange({
                        target: {
                            name: 'customLocation',
                            value: {
                                name: place.name,
                                address: place.formatted_address,
                                coordinates: {
                                    lat: location.lat(),
                                    lng: location.lng()
                                }
                            }
                        }
                    });

                    if (locationNameInputRef.current) {
                        locationNameInputRef.current.value = place.name;
                    }
                }
            }
        } catch (error) {
            console.error('Błąd podczas przetwarzania linku Google Maps:', error);
        }
    };

    // Obsługa przeciągania nagród
    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(newRegularTournament.prizes);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Aktualizuj kolejność
        const updatedPrizes = items.map((item, index) => ({
            ...item,
            order: index
        }));

        handleInputChange({
            target: {
                name: 'prizes',
                value: updatedPrizes
            }
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'rgb(17, 24, 39)',
                    color: 'white',
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: '1px solid rgb(55, 65, 81)',
                pb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: 'rgb(196, 181, 253)' }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: '600' }}>
                    {isEditing ? 'Edytuj turniej regularny' : 'Dodaj turniej regularny'}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ p: 3, overflow: 'auto' }}>
                <Grid container spacing={2}>
                    {/* Informacja o turniejach regularnych */}
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: 'rgb(147, 197, 253)',
                            '& .MuiAlert-icon': {
                                color: 'rgb(147, 197, 253)'
                            }
                        }}>
                            <Typography variant="body2">
                                <strong>Turniej regularny</strong> - wydarzenie odbywające się co tydzień w określonym dniu. 
                                System automatycznie wygeneruje wydarzenia dla każdego tygodnia w kalendarzu.
                            </Typography>
                        </Alert>
                    </Grid>

                    {/* Podstawowe informacje */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Podstawowe informacje
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Nazwa turnieju*"
                            name="name"
                            value={newRegularTournament.name}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Dzień tygodnia*"
                            name="dayOfWeek"
                            value={newRegularTournament.dayOfWeek}
                            onChange={handleInputChange}
                            size="small"
                            select
                            sx={commonTextFieldStyles}
                        >
                            <MenuItem value="poniedziałek">Poniedziałek</MenuItem>
                            <MenuItem value="wtorek">Wtorek</MenuItem>
                            <MenuItem value="środa">Środa</MenuItem>
                            <MenuItem value="czwartek">Czwartek</MenuItem>
                            <MenuItem value="piątek">Piątek</MenuItem>
                            <MenuItem value="sobota">Sobota</MenuItem>
                            <MenuItem value="niedziela">Niedziela</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Godzina*"
                            name="time"
                            value={newRegularTournament.time}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Liczba dni"
                            name="duration"
                            type="number"
                            value={newRegularTournament.duration || 1}
                            onChange={handleInputChange}
                            size="small"
                            InputProps={{
                                inputProps: { min: 1, max: 7 }
                            }}
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Miasto*"
                            name="city"
                            value={newRegularTournament.city}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Cena"
                            name="price"
                            value={newRegularTournament.price}
                            onChange={handleInputChange}
                            size="small"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">zł</InputAdornment>,
                            }}
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Szczegóły turnieju */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Szczegóły turnieju
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Format"
                            name="format"
                            value={newRegularTournament.format}
                            onChange={handleInputChange}
                            size="small"
                            select
                            sx={commonTextFieldStyles}
                        >
                            <MenuItem value="Core Constructed">Core Constructed</MenuItem>
                            <MenuItem value="Sealed">Sealed</MenuItem>
                            <MenuItem value="Draft">Draft</MenuItem>
                            <MenuItem value="Pack Rush">Pack Rush</MenuItem>
                            <MenuItem value="Custom">Custom</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Ranga"
                            name="rank"
                            value={newRegularTournament.rank}
                            onChange={handleInputChange}
                            size="small"
                            select
                            sx={commonTextFieldStyles}
                        >
                            <MenuItem value="LOCAL">Lokalny</MenuItem>
                            <MenuItem value="REGIONAL">Regionalny</MenuItem>
                            <MenuItem value="NATIONAL">Krajowy</MenuItem>
                            <MenuItem value="INTERNATIONAL">Międzynarodowy</MenuItem>
                            <MenuItem value="FOREIGN">Zagraniczny</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Struktura turnieju"
                            name="structure"
                            value={newRegularTournament.structure}
                            onChange={handleInputChange}
                            size="small"
                            multiline
                            rows={2}
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Linki */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Linki
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Link do wydarzenia*"
                            name="url"
                            value={newRegularTournament.url}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Link do melee.gg"
                            name="meleeLink"
                            value={newRegularTournament.meleeLink}
                            onChange={handleInputChange}
                            size="small"
                            sx={commonTextFieldStyles}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Organizator */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '500'
                            }}>
                                Organizator*
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleOpenOrganizerDialog}
                                sx={{
                                    borderColor: 'rgb(196, 181, 253)',
                                    color: 'rgb(196, 181, 253)',
                                    '&:hover': {
                                        borderColor: 'rgb(167, 139, 250)',
                                        backgroundColor: 'rgba(196, 181, 253, 0.1)'
                                    }
                                }}
                            >
                                Dodaj organizatora
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            options={organizers}
                            getOptionLabel={(option) => option.name}
                            value={newRegularTournament.organizer}
                            onChange={handleOrganizerSelect}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Wybierz organizatora"
                                    size="small"
                                    sx={commonTextFieldStyles}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Avatar
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            mr: 1,
                                            fontSize: '0.75rem',
                                            backgroundColor: 'rgb(196, 181, 253)'
                                        }}
                                    >
                                        {option.name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'white' }}>
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                            {option.city}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            sx={{
                                '& .MuiAutocomplete-popupIndicator': {
                                    color: 'rgb(196, 181, 253)'
                                }
                            }}
                        />
                    </Grid>

                    {/* Lokalizacja niestandardowa */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{
                            color: 'rgb(196, 181, 253)',
                            mb: 1,
                            fontWeight: '500'
                        }}>
                            Lokalizacja niestandardowa (opcjonalnie)
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Link do Google Maps"
                            placeholder="https://maps.google.com/..."
                            size="small"
                            sx={commonTextFieldStyles}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleGoogleMapsLink(e.target.value);
                                }
                            }}
                        />
                    </Grid>

                    {newRegularTournament.customLocation && (
                        <Grid item xs={12}>
                            <Card sx={{
                                backgroundColor: 'rgba(76, 29, 149, 0.1)',
                                border: '1px solid rgba(76, 29, 149, 0.3)'
                            }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: 'rgb(196, 181, 253)' }}>
                                            Lokalizacja niestandardowa
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleInputChange({
                                                target: { name: 'customLocation', value: null }
                                            })}
                                            sx={{ color: '#ef4444' }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'white', mb: 0.5 }}>
                                        <strong>Nazwa:</strong> {newRegularTournament.customLocation.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        <strong>Adres:</strong> {newRegularTournament.customLocation.address}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: 'rgb(55, 65, 81)' }} />
                    </Grid>

                    {/* Nagrody */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '500'
                            }}>
                                Nagrody
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddPrize}
                                sx={{
                                    borderColor: 'rgb(196, 181, 253)',
                                    color: 'rgb(196, 181, 253)',
                                    '&:hover': {
                                        borderColor: 'rgb(167, 139, 250)',
                                        backgroundColor: 'rgba(196, 181, 253, 0.1)'
                                    }
                                }}
                            >
                                Dodaj nagrodę
                            </Button>
                        </Box>
                    </Grid>

                    {newRegularTournament.prizes && newRegularTournament.prizes.length > 0 && (
                        <Grid item xs={12}>
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="prizes">
                                    {(provided) => (
                                        <Box
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            sx={{ space: 1 }}
                                        >
                                            {newRegularTournament.prizes.map((prize, index) => (
                                                <Draggable key={index} draggableId={`prize-${index}`} index={index}>
                                                    {(provided) => (
                                                        <Card
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            sx={{
                                                                backgroundColor: 'rgba(76, 29, 149, 0.1)',
                                                border: '1px solid rgba(76, 29, 149, 0.3)',
                                                mb: 1
                                            }}
                                                        >
                                                            <CardContent sx={{ p: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        {...provided.dragHandleProps}
                                                                        sx={{ color: 'rgb(196, 181, 253)' }}
                                                                    >
                                                                        <DragIndicatorIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <Typography variant="subtitle2" sx={{ color: 'rgb(196, 181, 253)' }}>
                                                                        Nagroda {index + 1}
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleRemovePrize(index)}
                                                                        sx={{ color: '#ef4444', ml: 'auto' }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                                <Grid container spacing={1}>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Pozycja"
                                                                            name={`prizes.${index}.position`}
                                                                            value={prize.position}
                                                                            onChange={(e) => handlePrizeChange(index, 'position', e.target.value)}
                                                                            size="small"
                                                                            sx={commonTextFieldStyles}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <TextField
                                                                            fullWidth
                                                                            label="Opis"
                                                                            name={`prizes.${index}.description`}
                                                                            value={prize.description}
                                                                            onChange={(e) => handlePrizeChange(index, 'description', e.target.value)}
                                                                            size="small"
                                                                            sx={commonTextFieldStyles}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </CardContent>
                                                        </Card>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </Box>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{
                borderTop: '1px solid rgb(55, 65, 81)',
                p: 2,
                gap: 1
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'rgb(156, 163, 175)',
                        '&:hover': {
                            backgroundColor: 'rgba(156, 163, 175, 0.1)'
                        }
                    }}
                >
                    Anuluj
                </Button>
                <Button
                    onClick={onSubmit}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{
                        backgroundColor: 'rgb(76, 29, 149)',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'rgb(91, 33, 182)'
                        }
                    }}
                >
                    {isEditing ? 'Zaktualizuj' : 'Dodaj'} turniej regularny
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddRegularTournamentForm; 